import { interestPointInput } from '@trafiku/contracts';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FormActions } from '@/components/form-actions';
import { ImageUpload } from '@/components/image-upload';
import { MapView } from '@/components/map/map-view';
import { OverviewMap } from '@/components/map/overview-map';
import { PointPicker } from '@/components/map/point-picker';
import { ResourceList } from '@/components/resource-list';
import { Field, Input, Switch, Textarea } from '@/components/ui/form';
import { Badge, Card, PageHeader, Spinner } from '@/components/ui/layout';
import { useFormState } from '@/lib/form';
import { t } from '@/lib/i18n';
import { pointsCollection } from '@/lib/network';
import { useItem, useSave } from '@/lib/resources';
import type { InterestPoint } from '@/lib/types';

export function InterestPointsPage() {
  return (
    <ResourceList<InterestPoint>
      title={t('Points of interest')}
      map={({ rows, hovered, hover }) => (
        <OverviewMap
          data={pointsCollection(
            rows,
            (p) => [Number(p.longitude), Number(p.latitude)],
            (p) => p.title,
            '#7c3aed',
          )}
          kind="point"
          hovered={hovered}
          onHover={hover}
          basePath="/interest-points"
        />
      )}
      resource="interest-points"
      basePath="/interest-points"
      newLabel={t('New point')}
      columns={[
        {
          header: t('Title'),
          cell: (p) => (
            <span className="flex items-center gap-2 font-medium">
              {p.icon && <img src={p.icon} alt="" className="size-6 rounded object-cover" />}
              {p.title}
            </span>
          ),
        },
        { header: t('Category'), cell: (p) => p.category || '—' },
        {
          header: t('Status'),
          cell: (p) =>
            p.is_active ? <Badge tone="green">{t('Active')}</Badge> : <Badge>{t('Hidden')}</Badge>,
        },
      ]}
    />
  );
}

export function InterestPointEditPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const item = useItem<InterestPoint>('interest-points', id);
  const save = useSave<InterestPoint>('interest-points', id);
  const navigate = useNavigate();
  const form = useFormState({
    title: '',
    category: '',
    description: '',
    link: '',
    icon: '',
    photo: '',
    is_active: false,
    latitude: undefined as number | undefined,
    longitude: undefined as number | undefined,
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: load once per item
  useEffect(() => {
    const p = item.data;
    if (p) form.setValues({ ...p });
  }, [item.data]);
  if (!isNew && item.isLoading) return <Spinner />;
  const { values, set, errors } = form;
  const pos: [number, number] | undefined =
    values.longitude !== undefined && values.latitude !== undefined
      ? [values.longitude, values.latitude]
      : undefined;
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (await form.submit(interestPointInput, (d) => save.mutateAsync(d))) navigate('/interest-points');
      }}
    >
      <PageHeader title={isNew ? t('New point of interest') : values.title} back="/interest-points" />
      <div className="grid gap-6 lg:grid-cols-[400px_1fr]">
        <Card>
          <div className="space-y-4">
            <Field label={t('Title')} error={errors.title}>
              {(fid) => (
                <Input
                  id={fid}
                  value={values.title}
                  onChange={(e) => set('title', e.target.value)}
                  invalid={!!errors.title}
                />
              )}
            </Field>
            <Field label={t('Category')}>
              {(fid) => (
                <Input id={fid} value={values.category} onChange={(e) => set('category', e.target.value)} />
              )}
            </Field>
            <Field label={t('Description')} error={errors.description}>
              {(fid) => (
                <Textarea
                  id={fid}
                  value={values.description}
                  onChange={(e) => set('description', e.target.value)}
                  invalid={!!errors.description}
                />
              )}
            </Field>
            <Field label={t('Link')}>
              {(fid) => (
                <Input
                  id={fid}
                  value={values.link}
                  onChange={(e) => set('link', e.target.value)}
                  placeholder="https://"
                />
              )}
            </Field>
            <Field label={t('Icon')}>
              {() => (
                <ImageUpload kind="pointIcons" square value={values.icon} onChange={(v) => set('icon', v)} />
              )}
            </Field>
            <Field label={t('Photo')}>
              {() => (
                <ImageUpload kind="pointPhotos" value={values.photo} onChange={(v) => set('photo', v)} />
              )}
            </Field>
            <Field label={t('Position')} error={errors.latitude ?? errors.longitude}>
              {() => (
                <p className="font-mono text-xs text-slate-600">
                  {pos ? `${pos[1].toFixed(6)}, ${pos[0].toFixed(6)}` : t('Click on the map')}
                </p>
              )}
            </Field>
            <Switch checked={values.is_active} onChange={(v) => set('is_active', v)} label={t('Visible')} />
          </div>
          <div className="mt-6">
            <FormActions
              saving={save.isPending}
              resource="interest-points"
              id={id}
              backTo="/interest-points"
            />
          </div>
        </Card>
        <MapView className="h-[600px]" center={pos} zoom={pos ? 16 : 12.5}>
          <PointPicker
            value={pos}
            onChange={([lng, lat]) => {
              set('longitude', lng);
              set('latitude', lat);
            }}
          />
        </MapView>
      </div>
    </form>
  );
}
